import { AxiosError } from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
    DownloadImageRequestPayload,
    ImageAttributes,
    ImageProject,
    Transformation,
    UserUploadsResponse,
} from '../types/imagesType';
import { axiosInstance } from './axiosInstance';
import axios from 'axios';
import { CreateImageProjectPayload } from '../types/imagesType';
import { ImageLayout } from '../types/imageLayoutTypes';
import { ImageProjectResponse } from '../types/imagesType';
import {
    ImageData,
    ImageResponse,
    UpdateImageRequestPayload,
    UpdateImageRequestBody,
    UploadImagePayload,
} from '../types/imagesType';

export const downloadImage = createAsyncThunk<
    string,
    DownloadImageRequestPayload,
    { rejectValue: string }
>('images/downloadImage', async ({ imageId, imageFormat, watermarked }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(
            `images/${imageId}/download?watermark=${watermarked}&ext=${imageFormat}`,
            {
                responseType: 'blob',
            },
        );

        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `image.${imageFormat}`;
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return url;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            const axiosError = error as AxiosError<{ message: string }>;
            return rejectWithValue(axiosError.response?.data.message || 'Failed to download image');
        }
        return rejectWithValue('An unexpected error occurred while downloading the image');
    }
});

export const fetchUserUploads = createAsyncThunk(
    'images/fetchUserUploads',
    async (userId: string) => {
        try {
            const response = await axiosInstance.get<UserUploadsResponse>(
                `/users/${userId}/images`,
            );
            return response.data.data.map((item) => ({
                id: item.id,
                url: item.attributes.url,
                canvas_color: item.attributes.canvas_color,
                visual_settings: item.attributes.visual_settings,
                visual_effects: item.attributes.visual_effects,
                transformation: item.attributes.transformation,
                is_realism_mode: item.attributes.is_realism_mode,
                reference_settings: item.attributes.reference_settings,
                creativity_level: item.attributes.creativity_level,
                prompt: item.attributes.prompt,
                original_url: item.attributes.original_url,
                name: item.attributes.name,
            }));
        } catch (error) {
            console.error('Failed to fetch user uploads:', error);
            return [];
        }
    },
);

export const createImageProject = createAsyncThunk<
    ImageProjectResponse['data'],
    CreateImageProjectPayload,
    { rejectValue: string }
>(
    'imageProjects/createImageProject',
    async ({ title, slideshow, projectId }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post<ImageProjectResponse>('/image_projects/', {
                data: {
                    type: 'image_projects',
                    attributes: {
                        title,
                        slideshow,
                    },
                    relationships: {
                        project: {
                            data: {
                                type: 'projects',
                                id: projectId,
                            },
                        },
                    },
                },
            });
            console.log('response', response.data);
            return response.data.data;
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                return rejectWithValue(
                    error.response.data.message || 'Failed to create image project',
                );
            }
            return rejectWithValue('An unexpected error occurred');
        }
    },
);

export const fetchImageProject = createAsyncThunk<ImageProject, string, { rejectValue: string }>(
    'imageProjects/fetchImageProject',
    async (imageProjectId, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get<ImageProjectResponse>(
                `/image_projects/${imageProjectId}`,
            );
            const { data, included } = response.data;
            let layouts = null;
            let layoutsData: any = included;
            let images: any = included;

            if (layoutsData) {
                layouts = layoutsData
                    .filter((layout: any) => layout.type === 'image_layouts')
                    .map((layout: any) => {
                        return {
                            id: layout.id,
                            slug: layout.attributes.slug,
                            order: layout.attributes.order,
                            gap_size: layout.attributes.gap_size,
                            canvas_color: layout.attributes.canvas_color,
                            transformation: layout.attributes.transformation,
                            images: layout.relationships.images.data,
                            inline_text: layout.attributes.inline_text,
                        };
                    });
            }

            if (images) {
                images = images
                    .filter((image: any) => image.type === 'images')
                    .map((image: any) => ({
                        id: image.id,
                        name: image.attributes.name,
                        original_url: image.attributes.original_url,
                        url: image.attributes.url,
                        canvas_color: image.attributes.canvas_color,
                        visual_settings: image.attributes.visual_settings,
                        visual_effects: image.attributes.visual_effects,
                        transformation: image.attributes.transformation,
                        prompt: image.attributes.prompt,
                        is_realism_mode: image.attributes.is_realism_mode,
                        reference_settings: image.attributes.reference_settings,
                        creativity_level: image.attributes.creativity_level,
                    }));
            }

            return {
                id: data.id,
                title: data.attributes.title,
                slideshow: data.attributes.slideshow,
                projectId: data.relationships.project.data.id,
                layouts,
                allImagesForLayouts: images,
            };
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                return rejectWithValue(
                    error.response.data.message || 'Failed to fetch image project',
                );
            }
            return rejectWithValue('An unexpected error occurred');
        }
    },
);

export const updateImage = createAsyncThunk<ImageResponse, UpdateImageRequestPayload>(
    'image/updateImage',
    async (payload) => {
        const {
            imageId,
            canvas_color,
            visual_settings,
            visual_effects,
            transformation,
            creativity_level,
            is_realism_mode,
            reference_settings,
            prompt,
        } = payload;

        const attributes: Record<string, any> = {};

        if (canvas_color) {
            attributes['canvas_color'] = {
                red: canvas_color.red,
                green: canvas_color.green,
                blue: canvas_color.blue,
                gradient: canvas_color.gradient != '' ? canvas_color.gradient : 'none',
                transparency: canvas_color.transparency,
            };
        }

        if (visual_settings) {
            attributes['visual_settings'] = {
                brightness: visual_settings.brightness,
                contrast: visual_settings.contrast,
                highlights: visual_settings.highlights,
                shadows: visual_settings.shadows,
                vibrance: visual_settings.vibrance,
                saturation: visual_settings.saturation,
                clarity: visual_settings.clarity,
                hue: visual_settings.hue,
                blacks: visual_settings.blacks,
                white: visual_settings.white,
                vignette: visual_settings.vignette,
                grain: visual_settings.grain,
                red: visual_settings.red,
                green: visual_settings.green,
                blue: visual_settings.blue,
            };
        }

        if (visual_effects) {
            attributes['visual_effects'] = {
                name: visual_effects.name,
                intensity: visual_effects.intensity,
            };
            if (canvas_color) {
                attributes['canvas_color'] = {
                    ...canvas_color,
                    gradient: canvas_color.gradient != '' ? canvas_color.gradient : 'none',
                };
            }
        }

        if (transformation) {
            attributes['transformation'] = {
                width: transformation.width,
                height: transformation.height,
                rotation: transformation.rotation,
                flip: transformation.flip,
                crop: transformation.crop,
                presets: transformation.presets,
                x: transformation.x,
                y: transformation.y,
            };
        }

        if (creativity_level) {
            attributes['creativity_level'] = creativity_level;
        }

        if (is_realism_mode) {
            attributes['is_realism_mode'] = is_realism_mode;
        }

        if (reference_settings) {
            attributes['reference_settings'] = {
                use_object: reference_settings.use_object,
                restyle_entire: reference_settings.restyle_entire,
                use_background: reference_settings.use_background,
                use_posture: reference_settings.use_posture,
            };
        }

        if (prompt) {
            attributes['prompt'] = prompt;
        }

        const body: UpdateImageRequestBody = {
            data: {
                type: 'images',
                id: imageId,
                attributes: attributes,
            },
        };

        try {
            const response = await axiosInstance.patch<ImageResponse>(`images/${imageId}`, body);
            return response.data;
        } catch (error: any) {
            const message = error.response?.data?.message || error.message || 'Unknown error';
            throw new Error(`Failed to update image: ${message}`);
        }
    },
);

export const updateImageUrl = createAsyncThunk<ImageResponse, { imageId: string; file: File }>(
    'image/updateImageUrl',
    async (payload) => {
        const { imageId, file } = payload;
        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await axiosInstance.post<ImageResponse>(
                `images/${imageId}?_method=PUT`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                },
            );
            console.log('response.data', response.data);
            return response.data;
        } catch (error: any) {
            const message = error.response?.data?.message || error.message || 'Unknown error';
            throw new Error(`Failed to update image: ${message}`);
        }
    },
);

export const enhanceImageUrl = createAsyncThunk<ImageResponse, { imageId: string }>(
    'image/enhanceImageUrl',
    async (payload) => {
        try {
            const response = await axiosInstance.patch<ImageResponse>(
                `images/${payload.imageId}/enhance`,
            );
            return response.data;
        } catch (error: any) {
            const message = error.response?.data?.message || error.message || 'Unknown error';
            throw new Error(`Failed to update image: ${message}`);
        }
    },
);

export const resetImageUrl = createAsyncThunk<ImageResponse, { imageId: string }>(
    'image/resetImageUrl',
    async (payload) => {
        try {
            const response = await axiosInstance.patch<ImageResponse>(
                `images/${payload.imageId}/reset`,
            );
            return response.data;
        } catch (error: any) {
            const message = error.response?.data?.message || error.message || 'Unknown error';
            throw new Error(`Failed to reset image: ${message}`);
        }
    },
);

export const wizardImageUrl = createAsyncThunk<
    ImageResponse,
    { imageId: string; input: string; creativity: number }
>('image/wizardImageUrl', async ({ imageId, input, creativity }) => {
    try {
        const payload = {
            data: {
                type: 'images',
                id: imageId,
                attributes: {
                    input: input,
                    creativity: creativity,
                },
            },
        };

        const response = await axiosInstance.patch<ImageResponse>(
            `images/${imageId}/wizard`,
            payload,
        );

        return response.data;
    } catch (error: any) {
        const message = error.response?.data?.message || error.message || 'Unknown error';
        throw new Error(`Failed to update image: ${message}`);
    }
});

export const removeBackgroundImageUrl = createAsyncThunk<ImageResponse, { imageId: string }>(
    'image/removeBackgroundImageUrl',
    async ({ imageId }) => {
        try {
            const response = await axiosInstance.patch<ImageResponse>(
                `images/${imageId}/remove-background`,
            );

            return response.data;
        } catch (error: any) {
            const message = error.response?.data?.message || error.message || 'Unknown error';
            throw new Error(`Failed to update image: ${message}`);
        }
    },
);

export const uploadImage = createAsyncThunk<
    ImageAttributes,
    UploadImagePayload,
    { rejectValue: string }
>('images/uploadImage', async ({ file }, { rejectWithValue }) => {
    try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await axiosInstance.post<ImageResponse>('/images', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        const { data } = response.data;
        return {
            id: data.id,
            name: 'image',
            original_url: data.attributes.original_url,
            url: data.attributes.url,
            canvas_color:
                data.attributes.canvas_color != null
                    ? data.attributes.canvas_color
                    : { red: 255, green: 255, blue: 255, gradient: 'none', transparency: 0 },
            visual_settings:
                data.attributes.visual_settings != null
                    ? data.attributes.visual_settings
                    : {
                          brightness: 0,
                          contrast: 0,
                          highlights: 0,
                          shadows: 0,
                          vibrance: 0,
                          saturation: 0,
                          clarity: 0,
                          hue: 0,
                          blacks: 0,
                          white: 0,
                          vignette: 0,
                          grain: 0,
                          red: 1,
                          green: 1,
                          blue: 1,
                      },
            visual_effects:
                data.attributes.visual_effects != null
                    ? data.attributes.visual_effects
                    : { name: 'none', intensity: 0 },
            transformation: data.attributes.transformation,
            prompt: data.attributes.prompt,
            is_realism_mode: data.attributes.is_realism_mode,
            reference_settings: data.attributes.reference_settings,
            creativity_level:
                data.attributes.creativity_level != null ? data.attributes.creativity_level : 0,
        };
    } catch (error) {
        console.log('Error occurred while uploading the image');
        if (axios.isAxiosError(error) && error.response) {
            const axiosError = error as AxiosError<{ message: string }>;
            return rejectWithValue(axiosError.response?.data.message || 'Failed to upload image');
        }
        return rejectWithValue('An unexpected error occurred');
    }
});

interface UpdateImageProjectPayload {
    imageProjectId: string;
    title?: string;
    slideshow?: {
        enabled: boolean;
        count: number;
    };
}

export const updateImageProject = createAsyncThunk<
    ImageProject,
    UpdateImageProjectPayload,
    { rejectValue: string }
>(
    'imageProjects/updateImageProject',
    async ({ imageProjectId, title, slideshow }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.patch<ImageProjectResponse>(
                `/image_projects/${imageProjectId}`,
                {
                    data: {
                        type: 'image_projects',
                        id: imageProjectId,
                        attributes: {
                            ...(title && { title }),
                            ...(slideshow && { slideshow }),
                        },
                    },
                },
            );
            const { data, included } = response.data;
            let layouts = null;
            let layoutsData: any = included;

            if (layoutsData) {
                layouts = layoutsData.map((layout: any) => ({
                    id: layout.id,
                    slug: layout.attributes.slug,
                    order: layout.attributes.order,
                    gap_size: layout.attributes.gap_size,
                    canvas_color: layout.attributes.canvas_color,
                    transformation: layout.attributes.transformation,
                }));
            }

            return {
                id: data.id,
                title: data.attributes.title,
                slideshow: data.attributes.slideshow,
                projectId: data.relationships.project.data.id,
                layouts,
                allImagesForLayouts: [],
            };
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                return rejectWithValue(
                    error.response.data.message || 'Failed to update image project',
                );
            }
            return rejectWithValue('An unexpected error occurred while updating the image project');
        }
    },
);

export const deleteImage = createAsyncThunk<string, string, { rejectValue: string }>(
    'images/deleteImage',
    async (imageId, { rejectWithValue }) => {
        try {
            await axiosInstance.delete(`/images/${imageId}`);
            return imageId;
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                return rejectWithValue(error.response.data.message || 'Failed to delete image');
            }
            return rejectWithValue('An unexpected error occurred while deleting the image');
        }
    },
);

export const downloadAllImages = createAsyncThunk<
    string,
    { layoutId: string; imageFormat: string; watermarked: boolean },
    { rejectValue: string }
>(
    'images/downloadAllImages',
    async ({ layoutId, imageFormat, watermarked }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(
                `image_layouts/${layoutId}/download-zip?watermark=${watermarked}&ext=${imageFormat}`,
                {
                    data: {
                        type: 'image_download',
                        attributes: {
                            image_format: imageFormat,
                            watermarked: watermarked,
                        },
                    },
                    responseType: 'blob',
                },
            );
            const blob = new Blob([response.data], { type: response.headers['content-type'] });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `images.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            return url;
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                return rejectWithValue(
                    error.response.data.message || 'Failed to download all images',
                );
            }
            return rejectWithValue('An unexpected error occurred while downloading all images');
        }
    },
);

export const generateImage = createAsyncThunk<
    ImageAttributes,
    { prompt: string; creativity_level: number; is_realism_mode: boolean },
    { rejectValue: string }
>(
    'images/generateImage',
    async ({ prompt, creativity_level, is_realism_mode }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post<ImageResponse>('/images/generate', {
                data: {
                    type: 'images',
                    attributes: {
                        prompt,
                        creativity_level,
                        is_realism_mode,
                    },
                },
            });
            const { data } = response.data;
            return {
                id: data.id,
                name: 'image',
                original_url: data.attributes.original_url,
                url: data.attributes.url,
                canvas_color: data.attributes.canvas_color || {
                    red: 255,
                    green: 255,
                    blue: 255,
                    gradient: 'none',
                    transparency: 0,
                },
                visual_settings: data.attributes.visual_settings || {
                    brightness: 0,
                    contrast: 0,
                    highlights: 0,
                    shadows: 0,
                    vibrance: 0,
                    saturation: 0,
                    clarity: 0,
                    hue: 0,
                    blacks: 0,
                    white: 0,
                    vignette: 0,
                    grain: 0,
                    red: 1,
                    green: 1,
                    blue: 1,
                },
                visual_effects: data.attributes.visual_effects || { name: 'none', intensity: 0 },
                transformation: data.attributes.transformation,
                prompt: data.attributes.prompt,
                is_realism_mode: data.attributes.is_realism_mode,
                reference_settings: data.attributes.reference_settings,
                creativity_level: data.attributes.creativity_level,
            };
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                return rejectWithValue(error.response?.data.message || 'Failed to generate image');
            }
            return rejectWithValue('An unexpected error occurred');
        }
    },
);

export const updateLayoutImage = createAsyncThunk<
    { projectId: string; imageId: string; url: string },
    { layoutId: string; file: File },
    { rejectValue: string }
>('images/updateLayoutImage', async ({ layoutId, file }, { rejectWithValue }) => {
    try {
        const formData = new FormData();
        formData.append('image', file);
        const response = await axiosInstance.post(`/image_layouts/${layoutId}/image`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        return {
            projectId: response.data.data.relationships.project.data.id,
            imageId: response.data.data.id,
            url: response.data.data.attributes.image_url,
        };
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            return rejectWithValue(error.response.data.message || 'Failed to update layout image');
        }
        return rejectWithValue('An unexpected error occurred while updating layout image');
    }
});

export const downloadLayoutImage = createAsyncThunk<
    string,
    { layoutId: string; watermark?: boolean; ext?: string },
    { rejectValue: string }
>('images/downloadImage', async ({ layoutId, watermark, ext }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(
            `image_layouts/${layoutId}/download?watermark=${watermark}&ext=${ext}`,
            {
                data: {
                    type: 'image_download',
                    attributes: {
                        image_format: ext,
                        watermarked: watermark,
                    },
                },
                responseType: 'blob',
            },
        );
        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `image.${ext}`;
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return url;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            const axiosError = error as AxiosError<{ message: string }>;
            return rejectWithValue(axiosError.response?.data.message || 'Failed to download image');
        }
        return rejectWithValue('An unexpected error occurred while downloading the image');
    }
});

export const updateZoom = createAsyncThunk<Transformation, UpdateImageRequestPayload>(
    'image/updateZoom',
    async (payload) => {
        const { imageId, transformation } = payload;

        const body: UpdateImageRequestBody = {
            data: {
                type: 'images',
                id: imageId,
                attributes: { transformation: transformation },
            },
        };

        try {
            const response = await axiosInstance.patch<ImageResponse>(`images/${imageId}`, body);
            return response.data.data.attributes.transformation;
        } catch (error: any) {
            const message = error.response?.data?.message || error.message || 'Unknown error';
            throw new Error(`Failed to update image: ${message}`);
        }
    },
);
export const updatePosition = createAsyncThunk<Transformation, UpdateImageRequestPayload>(
    'image/updatePosition',
    async (payload) => {
        const { imageId, transformation } = payload;

        const body: UpdateImageRequestBody = {
            data: {
                type: 'images',
                id: imageId,
                attributes: { transformation: transformation },
            },
        };

        try {
            const response = await axiosInstance.patch<ImageResponse>(`images/${imageId}`, body);
            return response.data.data.attributes.transformation;
        } catch (error: any) {
            const message = error.response?.data?.message || error.message || 'Unknown error';
            throw new Error(`Failed to update image: ${message}`);
        }
    },
);

export const updateTransformation = createAsyncThunk<Transformation, UpdateImageRequestPayload>(
    'image/updateTransformation',
    async (payload) => {
        const { imageId, transformation } = payload;

        const body: UpdateImageRequestBody = {
            data: {
                type: 'images',
                id: imageId,
                attributes: { transformation: transformation },
            },
        };

        try {
            const response = await axiosInstance.patch<ImageResponse>(`images/${imageId}`, body);
            return response.data.data.attributes.transformation;
        } catch (error: any) {
            const message = error.response?.data?.message || error.message || 'Unknown error';
            throw new Error(`Failed to update image: ${message}`);
        }
    },
);
